import React, { useCallback, useEffect, useState } from "react";
import { Calendar, CheckCircle2, AlertTriangle, RefreshCw, Link2, Unplug, PlugZap, ShieldCheck, XCircle } from "lucide-react";
import { PageHeader, Button, Card, CardContent, Select, StatusBadge, LoadingBlock, ConfirmDialog, toast } from "@/components/ds";
import { api, formatApiError, type CalendarConnection, type GoogleCalendarItem, type CalendarSyncLog } from "@/lib/api";
import { IntegrationMarketplace } from "./IntegrationMarketplace";

function fmt(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function HealthPill({ conn }: { conn: CalendarConnection }) {
  const map: Record<string, { tone: "green" | "amber" | "red" | "slate"; label: string; icon: typeof CheckCircle2 }> = {
    connected: { tone: "green", label: "Connected", icon: CheckCircle2 },
    needs_attention: { tone: "amber", label: "Needs attention", icon: AlertTriangle },
    revoked: { tone: "red", label: "Access revoked", icon: XCircle },
    error: { tone: "red", label: "Error", icon: XCircle },
    disconnected: { tone: "slate", label: "Not connected", icon: Unplug },
  };
  const effective = conn.status === "connected" && conn.last_sync_status === "failed" ? "needs_attention" : conn.status;
  const m = map[effective] ?? map.disconnected;
  const Icon = m.icon;
  return <StatusBadge tone={m.tone} data-testid="gcal-health"><Icon className="h-3.5 w-3.5" /> {m.label}</StatusBadge>;
}

export default function Integrations() {
  const [conn, setConn] = useState<CalendarConnection | null>(null);
  const [calendars, setCalendars] = useState<GoogleCalendarItem[] | null>(null);
  const [logs, setLogs] = useState<CalendarSyncLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(async () => {
    try {
      const c = await api.gcalStatus();
      setConn(c);
      if (c.connected) {
        api.gcalCalendars().then((r) => setCalendars(r.calendars)).catch(() => setCalendars([]));
        api.gcalLogs().then((r) => setLogs(r.logs)).catch(() => {});
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Reflect the OAuth redirect result (?google=connected|denied|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get("google");
    if (g) {
      if (g === "connected") toast.success("Google Calendar connected");
      else if (g === "denied") toast.error("Google authorization was denied");
      else toast.error("Could not connect Google Calendar");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try { const { authorization_url } = await api.gcalConnect(); window.location.href = authorization_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const choose = async (id: string) => {
    setBusy(true);
    try { await api.gcalChoose(id); toast.success("Calendar selected"); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const r = await api.gcalTest();
      if (r.ok) { setTestResult(`OK — ${r.calendar_count} calendars, ${r.writable ? "writable" : "read-only"}`); toast.success("Connection healthy"); }
      else { setTestResult(`Failed — ${r.error ?? "unknown error"}`); toast.error("Connection test failed"); }
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { const r = await api.gcalRetrySync(); toast.success(`Retried ${r.attempted}: ${r.synced} synced, ${r.still_failed} failed`); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.gcalDisconnect(); toast.success("Disconnected"); setCalendars(null); setLogs([]); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!conn) return <div className="p-2"><LoadingBlock rows={5} /></div>;

  const failedSync = conn.status === "revoked" || conn.last_sync_status === "failed" || conn.status === "error";

  return (
    <div data-testid="page-integrations" className="space-y-6">
      <PageHeader title="Integrations" description="Connect the providers that power ResFil AI." testid="integrations-header" />

      {!conn.configured && (
        <Card><CardContent className="p-5" data-testid="gcal-not-configured">
          <p className="text-sm text-amber-700">Google Calendar isn't configured on the server yet.</p>
        </CardContent></Card>
      )}

      <Card>
        <CardContent className="space-y-5 p-6" data-testid="gcal-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">Google Calendar</h3>
                  <HealthPill conn={conn} />
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  Two-way sync: booked appointments appear on your calendar, and your calendar's busy times block new bookings. Your internal schedule stays the source of truth.
                </p>
                {conn.connected && <p className="mt-2 text-sm text-slate-600" data-testid="gcal-account">Account: <span className="font-medium">{conn.google_email ?? "—"}</span></p>}
              </div>
            </div>
            <div className="shrink-0">
              {conn.connected ? (
                <Button variant="outline" onClick={() => setConfirmDisconnect(true)} disabled={busy} data-testid="gcal-disconnect-btn"><Unplug className="h-4 w-4" /> Disconnect</Button>
              ) : (
                <Button onClick={connect} disabled={busy || !conn.configured} data-testid="gcal-connect-btn"><Link2 className="h-4 w-4" /> Connect Google</Button>
              )}
            </div>
          </div>

          {conn.connected && (
            <>
              {failedSync && (
                <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="gcal-alert">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">{conn.status === "revoked" ? "Google access was revoked" : "A calendar sync failed"}</p>
                      <p className="text-xs text-amber-700">{conn.last_error ?? "Your appointments are safe in ResFil AI. Retry once Google is reachable."}</p>
                    </div>
                  </div>
                  {conn.status === "revoked"
                    ? <Button size="sm" onClick={connect} disabled={busy} data-testid="gcal-reconnect-btn"><PlugZap className="h-4 w-4" /> Reconnect</Button>
                    : <Button size="sm" onClick={retry} disabled={busy} data-testid="gcal-retry-btn"><RefreshCw className="h-4 w-4" /> Retry sync</Button>}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase text-slate-400">Calendar</p>
                  {calendars === null ? <LoadingBlock rows={1} /> : (
                    <Select value={conn.calendar_id ?? "primary"} onChange={choose} testid="gcal-calendar-select"
                      options={calendars.map((c) => ({ value: c.id, label: c.summary + (c.primary ? " (primary)" : "") }))} />
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="gcal-health-panel">
                  <p className="text-xs text-slate-500">Connected {fmt(conn.connected_at)}</p>
                  <p className="text-xs text-slate-500">Last sync: <span className="font-medium text-slate-700">{fmt(conn.last_sync_at)}</span>{conn.last_sync_status ? ` · ${conn.last_sync_status}` : ""}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <Button variant="outline" size="sm" onClick={test} disabled={busy} data-testid="gcal-test-btn"><ShieldCheck className="h-4 w-4" /> Test connection</Button>
                <Button variant="outline" size="sm" onClick={retry} disabled={busy} data-testid="gcal-retry-sync-btn"><RefreshCw className="h-4 w-4" /> Retry failed syncs</Button>
                {testResult && <span className="text-sm text-slate-600" data-testid="gcal-test-result">{testResult}</span>}
              </div>

              {logs.length > 0 && (
                <div data-testid="gcal-logs">
                  <p className="mb-2 text-sm font-semibold text-slate-800">Recent sync activity</p>
                  <div className="space-y-1">
                    {logs.slice(0, 8).map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-1.5 text-xs" data-testid={`gcal-log-${l.id}`}>
                        <span className="flex items-center gap-2">
                          {l.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                          <span className="font-medium text-slate-700">{l.action}</span>
                          <span className="text-slate-400">{l.appointment_id}</span>
                        </span>
                        <span className="text-slate-400">{l.error ? l.error.slice(0, 40) : fmt(l.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect} title="Disconnect Google Calendar?"
        description="New appointments will no longer sync to Google, and calendar busy times won't block bookings. Your ResFil AI schedule is unaffected." confirmLabel="Disconnect" destructive
        onConfirm={disconnect} testid="gcal-disconnect-confirm" />

      <IntegrationMarketplace />
    </div>
  );
}
